/**
 * React NodeView for `fileEmbed` — Phase 4c inline atomic embed.
 *
 * Renders the chip: file-type icon (resolved by MIME / extension) +
 * file name + human-readable size + download button. The download
 * button fetches a fresh presigned GET URL (5-min TTL) and opens it in
 * a new tab via `window.open(url, '_blank')` — the BE auto-attaches
 * `Content-Disposition: attachment` for non-image MIME types so the
 * browser triggers a download rather than rendering inline.
 *
 * Visual contract:
 *   - Inline chip following the canonical `wiki-inline-chip` recipe.
 *   - File icon at the start (Lucide `<File />` family by MIME).
 *   - File name truncates at the chip's max-width (configured at the
 *     CSS layer); the `title` attr carries the full name for hover.
 *   - Size column at the trailing edge — bold for legibility, smaller
 *     em-scale so it doesn't dominate the name.
 *
 * Locale-aware rendering: size formatting via `formatBytes(sizeBytes,
 * i18n.language)`; the formatter routes through `Intl.NumberFormat`
 * for locale-aware decimal separators (Spanish: `1,2 MB` vs English:
 * `1.2 MB`). Re-renders on locale change via the `useTranslation` hook.
 */

import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import {
  AlertCircle,
  File,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Loader2,
  type LucideIcon,
  RotateCcw,
  Upload,
  X,
} from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { uploadPercent } from '@/features/uploads/UploadProgressBar'
import { useUploadProgress } from '@/features/wiki/hooks/use-upload-guards'
import {
  CANCEL_EMBED_UPLOAD_EVENT,
  type CancelEmbedUploadDetail,
  DISMISS_EMBED_UPLOAD_EVENT,
  type EmbedUploadActionDetail,
  OPEN_FILE_LIGHTBOX_EVENT,
  OPEN_FILE_UPLOAD_EVENT,
  type OpenEmbedDetail,
  type OpenFileLightboxDetail,
  RETRY_EMBED_UPLOAD_EVENT,
} from '@/features/wiki/lib/embed-events'
import { formatBytes } from '@/features/wiki/lib/format-bytes'
import { formatPercent } from '@/i18n/formatters'

/**
 * MIME-prefix → Lucide icon map. The chip icon is purely indicative —
 * the BE attachment row is the source of truth. Falls back to the
 * generic `<File />` glyph for unknown types.
 */
function resolveIcon(mimeType: string | null, fileName: string): LucideIcon {
  if (mimeType) {
    if (mimeType.startsWith('image/')) return FileImage
    if (mimeType.startsWith('video/')) return FileVideo
    if (mimeType.startsWith('audio/')) return FileAudio
    if (mimeType === 'application/pdf') return FileText
    if (mimeType.startsWith('text/')) return FileText
    if (
      mimeType === 'application/zip' ||
      mimeType === 'application/x-zip-compressed' ||
      mimeType === 'application/x-tar' ||
      mimeType === 'application/gzip'
    ) {
      return FileArchive
    }
    if (
      mimeType === 'application/vnd.ms-excel' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      mimeType === 'text/csv'
    ) {
      return FileSpreadsheet
    }
  }
  // Extension fallback for cases where the BE didn't recover the MIME
  // (some user-agents drop it on .heic / .avif paths).
  const ext = fileName.split('.').pop()?.toLowerCase() ?? ''
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'heic'].includes(ext)) return FileImage
  if (['mp4', 'webm', 'mov', 'mkv'].includes(ext)) return FileVideo
  if (['mp3', 'wav', 'm4a'].includes(ext)) return FileAudio
  if (['pdf', 'txt', 'md'].includes(ext)) return FileText
  if (['zip', 'tar', 'gz', '7z'].includes(ext)) return FileArchive
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet
  return File
}

export function FileEmbedView({
  node,
  getPos,
  editor,
  deleteNode,
}: ReactNodeViewProps<HTMLElement>) {
  const { t, i18n } = useTranslation('wiki')
  const fileId = (node.attrs.fileId as string | null) ?? null
  const fileName = (node.attrs.fileName as string) ?? ''
  const sizeBytes = (node.attrs.sizeBytes as number | null) ?? null
  const mimeType = (node.attrs.mimeType as string | null) ?? null
  const uploadingPlaceholderId = (node.attrs.uploadingPlaceholderId as string | null) ?? null
  const uploadPercentValue = uploadPercent(useUploadProgress(uploadingPlaceholderId))
  const uploadingFailed = (node.attrs.uploadingFailed as boolean) ?? false

  const Icon = useMemo(() => resolveIcon(mimeType, fileName), [mimeType, fileName])

  // Lightbox-open + key/click handlers. MUST live above the
  // `if (!fileId)` placeholder early return so the hook count stays
  // stable across placeholder ↔ ready renders. Drop this hoist and an
  // upload transitioning fileId from `null` → `<uuid>` flips the hook
  // count mid-component-instance and React throws
  // "rendered more hooks than during the previous render".
  const openLightbox = useCallback(() => {
    if (!fileId || typeof window === 'undefined') return
    window.dispatchEvent(
      new CustomEvent<OpenFileLightboxDetail>(OPEN_FILE_LIGHTBOX_EVENT, {
        detail: {
          attachmentId: fileId,
          fileName,
          sizeBytes: sizeBytes ?? 0,
          mimeType: mimeType ?? '',
        },
      }),
    )
  }, [fileId, fileName, sizeBytes, mimeType])

  const onBodyKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      // Native <button> already handles Enter; Space defaults to keyup
      // on Chrome but mousedown on Firefox. Wire both explicitly so the
      // chip's open behavior is consistent across browsers + so we can
      // call stopPropagation to keep ProseMirror's atomic-node selection
      // from competing with the synthetic event.
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        openLightbox()
      }
    },
    [openLightbox],
  )

  const onBodyClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      openLightbox()
    },
    [openLightbox],
  )

  // FAILED — the upload errored; the placeholder stays visible with
  // Retry / Dismiss instead of silently disappearing (a vanished
  // skeleton reads as "the drop did nothing"). Checked before UPLOADING:
  // a failed node still carries its placeholder id. The orchestrator
  // kept the original File keyed on that id, so Retry re-runs the
  // network leg against this same node. Never persists — the strip
  // removes it from every flush / IDB write.
  if (uploadingPlaceholderId && uploadingFailed) {
    const dispatchAction = (eventName: string) => (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (typeof window === 'undefined') return
      window.dispatchEvent(
        new CustomEvent<EmbedUploadActionDetail>(eventName, {
          detail: { key: uploadingPlaceholderId },
        }),
      )
    }
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="file-embed"
        data-state="error"
        className="relative inline-flex align-baseline"
        contentEditable={false}
      >
        <span
          className="wiki-inline-chip inline-flex items-center gap-1 border-danger/40 text-fg-muted"
          title={fileName}
        >
          <AlertCircle aria-hidden="true" size={12} className="text-danger" />
          <span className="max-w-[18rem] truncate">{fileName}</span>
          <span className="text-danger">{t('docDrop.failedLabel')}</span>
        </span>
        <button
          type="button"
          onClick={dispatchAction(RETRY_EMBED_UPLOAD_EVENT)}
          onMouseDown={(e) => e.stopPropagation()}
          className="ms-1 inline-flex items-center gap-0.5 rounded p-0.5 text-xs font-medium text-fg-muted hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('docDrop.retry')}
          title={t('docDrop.retry')}
        >
          <RotateCcw aria-hidden="true" size={12} />
        </button>
        <button
          type="button"
          onClick={dispatchAction(DISMISS_EMBED_UPLOAD_EVENT)}
          onMouseDown={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center rounded p-0.5 text-fg-muted hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('docDrop.dismiss')}
          title={t('docDrop.dismiss')}
        >
          <X aria-hidden="true" size={12} />
        </button>
      </NodeViewWrapper>
    )
  }

  // UPLOADING — an OS-drop upload is in flight. Checked BEFORE the
  // `!fileId` picker placeholder below: an uploading node ALSO has
  // `fileId === null`, and the picker branch's reopen / remove buttons
  // are exactly the wrong affordances mid-upload. Cancel dispatches the
  // orchestrator's cancel event (it owns the AbortController + the
  // best-effort attachment-row cleanup). Plain inline handlers — no new
  // hooks, so the hook count stays stable across state flips.
  if (uploadingPlaceholderId) {
    const cancelUpload = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (typeof window === 'undefined') return
      window.dispatchEvent(
        new CustomEvent<CancelEmbedUploadDetail>(CANCEL_EMBED_UPLOAD_EVENT, {
          detail: { uploadingPlaceholderId },
        }),
      )
    }
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="file-embed"
        data-state="uploading"
        className="relative inline-flex align-baseline"
        contentEditable={false}
      >
        <span
          className="wiki-inline-chip inline-flex items-center gap-1 text-fg-muted"
          title={fileName}
        >
          <Loader2
            aria-hidden="true"
            size={12}
            className="animate-spin motion-reduce:animate-none"
          />
          <span className="max-w-[18rem] truncate">{fileName}</span>
          {/* A NUMBER, not a bar. This chip is one line of running text —
           * a 2px rule inside it reads as an underline of the filename,
           * and there is no vertical room for anything taller. The same
           * call the card file chip already made. Absent until the first
           * byte event: a zero would read as a stall, and the spinner
           * beside it is already the "started" signal. */}
          {uploadPercentValue !== null ? (
            <span className="tabular-nums">
              {formatPercent(uploadPercentValue / 100, i18n.language)}
            </span>
          ) : null}
          <span className="sr-only">{t('attachment.uploading')}</span>
        </span>
        <button
          type="button"
          onClick={cancelUpload}
          onMouseDown={(e) => e.stopPropagation()}
          className="ms-1 inline-flex items-center justify-center rounded p-0.5 text-fg-muted hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('attachment.cancel')}
          title={t('attachment.cancel')}
        >
          <X aria-hidden="true" size={12} />
        </button>
      </NodeViewWrapper>
    )
  }

  // Placeholder — file not yet picked / uploaded.
  // Clickable so the user can re-open the upload modal after dismissing
  // it accidentally; the inline remove button gives a clear way out
  // without needing Backspace. The chip stays inline (the node's schema
  // is inline-atomic) but uses real interactive `<button>` elements
  // instead of a passive `<span>` so it's reachable by mouse + keyboard.
  if (!fileId) {
    const reopenUploadModal = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (typeof window === 'undefined') return
      const pos = typeof getPos === 'function' ? getPos() : undefined
      if (typeof pos !== 'number') return
      window.dispatchEvent(
        new CustomEvent<OpenEmbedDetail>(OPEN_FILE_UPLOAD_EVENT, { detail: { pos } }),
      )
    }
    const removeNode = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (typeof deleteNode === 'function') {
        deleteNode()
        return
      }
      const pos = typeof getPos === 'function' ? getPos() : undefined
      if (typeof pos !== 'number' || !editor) return
      editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + node.nodeSize })
        .run()
    }
    // `onMouseDown.stopPropagation` is load-bearing on both buttons:
    // ProseMirror's selection-on-mousedown for atomic nodes runs at the
    // capture phase and intercepts click intent before React's
    // synthetic onClick fires. Without the stopPropagation, clicking
    // either button selects the node instead of triggering its action,
    // and the user reports the placeholder as un-dismissable except via
    // Backspace.
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="file-embed"
        data-state="placeholder"
        className="relative inline-flex align-baseline"
        contentEditable={false}
      >
        <button
          type="button"
          onClick={reopenUploadModal}
          onMouseDown={(e) => e.stopPropagation()}
          className="wiki-inline-chip inline-flex items-center gap-1 italic text-fg-subtle hover:text-fg"
          aria-label={t('file.placeholderAriaLabel', { defaultValue: 'Pick a file' })}
          title={t('file.placeholderHint', { defaultValue: 'Click to choose a file' })}
        >
          <Upload aria-hidden="true" size={12} />
          <span>{t('file.placeholder', { defaultValue: 'Pick a file…' })}</span>
        </button>
        <button
          type="button"
          onClick={removeNode}
          onMouseDown={(e) => e.stopPropagation()}
          className="ms-1 inline-flex items-center justify-center rounded p-0.5 text-fg-muted hover:bg-bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('file.placeholderRemove', { defaultValue: 'Remove' })}
          title={t('file.placeholderRemove', { defaultValue: 'Remove' })}
        >
          <X aria-hidden="true" size={12} />
        </button>
      </NodeViewWrapper>
    )
  }

  const ariaLabel = t('file.chipAriaLabel', {
    fileName,
    mimeType: mimeType ?? t('file.mimeUnknown', { defaultValue: 'file' }),
    defaultValue: 'File attachment: {fileName} ({mimeType}). Press Enter to preview.',
  })

  // Ready-state chip — single bounded interactive token, status-lozenge
  // shape with a 1px border. One <button> = one focus stop = one ARIA
  // name; the size text + download button moved into <WikiFileLightbox>.
  // `onMouseDown.stopPropagation` is load-bearing — ProseMirror's
  // selection-on-mousedown for atomic nodes runs at capture phase and
  // intercepts click intent before React's synthetic onClick fires.
  // Without it, clicking selects the node instead of opening the lightbox.
  return (
    <NodeViewWrapper
      as="span"
      data-block-type="file-embed"
      data-file-id={fileId}
      data-state="ready"
      className="relative inline-block align-baseline"
    >
      <button
        type="button"
        onClick={onBodyClick}
        onKeyDown={onBodyKeyDown}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={ariaLabel}
        title={fileName}
        className="wiki-inline-chip wiki-inline-chip--file"
      >
        <Icon aria-hidden="true" size={14} className="me-1 text-fg-muted" />
        <span className="max-w-[18rem] truncate text-fg">{fileName}</span>
        {sizeBytes !== null && (
          <span className="ms-1.5 shrink-0 text-[0.75em] text-fg-muted">
            {formatBytes(sizeBytes, i18n.language)}
          </span>
        )}
      </button>
    </NodeViewWrapper>
  )
}

FileEmbedView.displayName = 'FileEmbedView'
